import { useState } from 'react';
import { api, call } from '../platform/api.js';
import type { IntakeMeta } from '../../../api/src/catalog/intake.js';
import { anchorOf, type AnchorRect } from './option-sheet.js';
import { Icon } from './icons.js';

/**
 * EDITING THE SHAPE OF THE CATALOGUE WHERE THE CATALOGUE IS USED — maintainer request,
 * 30 Jul 2026: "we move the structure tab editing to the places where we actually use it."
 *
 * Until now a field could only be renamed, deleted or given a new option on the Structure
 * screen, three navigations away from the moment you notice the problem — you are standing
 * at the rack looking at "Materiaal" with no Denim in it. This puts rename, delete and
 * add-option on the field itself.
 *
 * ⚠️ THE STRUCTURE SCREEN STAYS for now (maintainer decision, same day): inline is proven
 * first, removed second. Nothing here deletes that screen, and both call the same
 * procedures, so the two cannot drift apart in behaviour.
 *
 * IT RENDERS FOR EVERY ROLE AND THE SERVER REFUSES NON-ADMINS — the standing rule, restated
 * here because this is now reachable by a student doing daily intake rather than only by
 * someone who navigated to an admin screen: client-side roles render, they do not gate. A
 * student who opens this gets a ledgered 403 and a plain sentence, the same answer the
 * Structure screen has always given.
 *
 * THE PENCIL IS NOT HOVER-ONLY. It was asked for as a hover affordance and it is one on a
 * mouse — but this is a phone-first app used in a basement, and `:hover` does not exist on
 * a touchscreen. On touch it is simply always present, faint. A control you can only reveal
 * with a pointer you do not have is not a control.
 */

export interface StructureTarget {
  key: string;
  label: string;
  options: string[];
}


/** The pencil that sits beside a field row and opens the menu. */
export function StructurePencil({
  label,
  onOpen,
}: {
  label: string;
  onOpen: (at: AnchorRect | null) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="structure-pencil"
      // Named, not just "Edit": with one pencil per field row, a screen reader reading
      // eight identical "Edit" buttons tells you nothing about which field you are on.
      aria-label={`Edit the field ${label}`}
      onClick={(e) => {
        // The row itself opens the VALUE picker. Without this, the pencil would open both.
        e.stopPropagation();
        onOpen(anchorOf(e));
      }}
    >
      <Icon name="pencil" />
    </button>
  );
}


/**
 * The menu itself: rename, add an option, remove options, delete the field. Anchored like
 * the option picker and deliberately the same visual object — it opens from the same kind
 * of tap in the same place, so it should not look like a different species of thing.
 */
/**
 * ⚠️ `StructureMenu` AND `StructurePencil` WERE REMOVED HERE IN 0.1.90, and the note stays
 * because the shape of the mistake is worth keeping.
 *
 * They were the pencil BESIDE each field row. 0.1.87 moved that act into the field's own
 * dropdown and stopped rendering them — but left the component, its state hook and its
 * tests in place. Nothing opened it: `structureFor` was initialised to null and every
 * `setStructureFor` call sat inside the menu's own render. So the app carried a fully
 * tested, completely unreachable third way to rename and delete a field, and its tests
 * went on passing, which is the reason nobody noticed.
 *
 * Removed together with the "Rename field" / "Delete field" footer (maintainer, 31 Jul
 * 2026), so those two acts now live in exactly ONE place: the pencil inside the field's
 * dropdown. `AddFieldButton` below was always reachable and stays.
 */

export function AddFieldButton({ onMeta }: { onMeta: (meta: IntakeMeta) => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [multi, setMulti] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const create = async (): Promise<void> => {
    const name = label.trim();
    if (name === '' || busy) return;
    setBusy(true);
    setProblem(null);
    const result = await call('createFieldDef', () =>
      api.createFieldDef.mutate({ label: name, kind: multi ? 'multi_select' : 'single_select' }),
    );
    setBusy(false);
    if (!result.ok) {
      setProblem(result.failure.message);
      return;
    }
    // `createFieldDef` answers with the new field AND the fresh meta; the others answer
    // with the meta alone. Unwrapped here rather than papered over with a cast.
    onMeta(result.data.meta);
    setLabel('');
    setOpen(false);
  };

  if (!open) {
    return (
      <p>
        <button type="button" className="structure-add" onClick={() => { setOpen(true); }}>
          <Icon name="plus" /> Add field
        </button>
      </p>
    );
  }

  return (
    <div className="structure-add-form">
      {problem !== null && <p role="alert">{problem}</p>}
      <p className="field">
        <label>
          Field name
          <input
            type="text"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
          />
        </label>
      </p>
      <p className="field">
        <label>
          <input
            type="checkbox"
            checked={multi}
            onChange={(e) => {
              setMulti(e.target.checked);
            }}
          />{' '}
          Allows several values at once
        </label>
      </p>
      <p>
        <button type="button" disabled={busy || label.trim() === ''} onClick={() => void create()}>
          Create field
        </button>{' '}
        <button type="button" onClick={() => { setOpen(false); setProblem(null); }}>
          Cancel
        </button>
      </p>
    </div>
  );
}
